import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { resolveChannel, channelMeta, displayTicketId } from '../utils/channels';
import {
  CheckCircleIcon, HandRaisedIcon, ArrowPathIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import ReviewItemModal from '../components/ReviewItemModal';

// Human-friendly labels + colours for the escalation_reason codes the backend
// stamps on a queue row (see email_processor._mark_escalated). A comma-joined
// reason ("dispute,callback") renders as two badges.
const REASON_META = {
  frustration:        { label: 'Frustrated',          classes: 'bg-red-100 text-red-800' },
  identity_challenge: { label: 'Wants a human',       classes: 'bg-amber-100 text-amber-800' },
  dispute:            { label: 'Dispute',             classes: 'bg-red-100 text-red-800' },
  billing:            { label: 'Billing query',       classes: 'bg-purple-100 text-purple-800' },
  payment:            { label: 'Payment allocation',  classes: 'bg-purple-100 text-purple-800' },
  callback:           { label: 'Callback',            classes: 'bg-blue-100 text-blue-800' },
  special_instruction:{ label: 'Special instruction', classes: 'bg-indigo-100 text-indigo-800' },
};

function reasonBadges(reason) {
  const codes = (reason || '').split(',').map((c) => c.trim()).filter(Boolean);
  if (codes.length === 0) return [{ label: 'Escalation', classes: 'bg-gray-100 text-gray-800' }];
  return codes.map((code) => REASON_META[code]
    || { label: code.replace(/_/g, ' '), classes: 'bg-gray-100 text-gray-800' });
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// Tell the sidebar badge to refresh right after we change the outstanding count.
function notifyChanged() {
  try { window.dispatchEvent(new Event('escalations-changed')); } catch (_) { /* noop */ }
}

export default function Escalations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  // The row currently opened in the shared review modal (null = closed).
  const [respondItem, setRespondItem] = useState(null);

  const fetchEscalations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiClient.get('/api/v1/queue/escalated');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!silent) toast.error('Could not load escalations');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEscalations(); }, [fetchEscalations]);

  // Silent auto-refresh every 45s while the tab is visible (parity with Queue),
  // paused while the respond modal is open so it never disrupts a reply.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (respondItem) return;
      fetchEscalations(true);
    };
    const id = setInterval(tick, 45000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchEscalations, respondItem]);

  const dropItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const markHandled = async (item) => {
    setResolvingId(item.id);
    try {
      await apiClient.post(`/api/v1/queue/${item.id}/resolve`);
      dropItem(item.id);
      notifyChanged();
      toast.success('Marked handled');
    } catch (err) {
      toast.error('Could not mark handled');
      fetchEscalations(true);
    } finally {
      setResolvingId(null);
    }
  };

  // After a reply is sent (or the item rejected) from the shared modal, it is
  // reviewed and drops off this list — refresh and update the sidebar badge.
  const onActioned = () => {
    if (respondItem) dropItem(respondItem.id);
    fetchEscalations(true);
    notifyChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <HandRaisedIcon className="h-6 w-6 text-red-600" />
            Escalated to person
            {items.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 text-sm font-semibold text-white bg-red-600 rounded-full">
                {items.length}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Conversations a person needs to take over. Reply right here, or mark handled once you've dealt with it.
          </p>
        </div>
        <button
          onClick={() => fetchEscalations()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 py-12 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg py-16 text-center">
          <CheckCircleIcon className="h-10 w-10 text-green-500 mx-auto" />
          <p className="mt-2 text-gray-700 font-medium">Nothing needs a person right now</p>
          <p className="text-sm text-gray-500">Escalated conversations will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Why</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">About</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Waiting</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => {
                const t = item.ticket || {};
                const channel = resolveChannel(t);
                const meta = channelMeta(channel);
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        {reasonBadges(item.escalation_reason).map((b, i) => (
                          <span key={i} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${b.classes}`}>
                            {b.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm font-medium text-gray-900">
                        {t.customer_name || t.customer_email || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">#{displayTicketId(t.freshdesk_ticket_id)}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.classes}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top max-w-md">
                      <div className="text-sm text-gray-700 truncate">
                        {item.summary || t.subject || t.body || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-sm text-gray-500">
                      {timeAgo(item.queued_at)}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => setRespondItem(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-michanic rounded-md hover:opacity-90"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                          Respond
                        </button>
                        <button
                          onClick={() => markHandled(item)}
                          disabled={resolvingId === item.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          {resolvingId === item.id ? 'Saving…' : 'Handled'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Respond — the exact same review modal the Review Queue's "View" opens. */}
      <ReviewItemModal
        item={respondItem}
        onClose={() => setRespondItem(null)}
        onActioned={onActioned}
      />
    </div>
  );
}
